import React, { useState, useEffect, useCallback } from 'react';
import { getFeatureAdoption } from '../../services/api';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';

const ACTION_LABELS = {
  screener_run: 'Run Screener',
  screener_save_params: 'Save Parameters',
  screener_delete_params: 'Delete Parameters',
  watchlist_run_simulation: 'Run Simulation',
  watchlist_simulate_group: 'Simulate Group',
  watchlist_run_bulk: 'Bulk Simulation',
  watchlist_refresh_contract: 'Refresh Contract',
  watchlist_refresh_group: 'Refresh Group',
  watchlist_create_group: 'Create Group',
  watchlist_update_group: 'Update Group',
  watchlist_delete_group: 'Delete Group',
  watchlist_assign_contracts: 'Assign Contracts',
  watchlist_remove_contract: 'Remove Contract',
  watchlist_create_contract: 'Create Contract',
  watchlist_delete_contract: 'Delete Contract',
  watchlist_reset_countdown: 'Reset Countdown',
  watchlist_update_target: 'Update Target',
};

const SCREENER_COLORS = ['#10b981', '#059669', '#047857'];
const WATCHLIST_COLORS = [
  '#3b82f6', '#2563eb', '#1d4ed8', '#1e40af', '#1e3a8a',
  '#60a5fa', '#93c5fd', '#3b82f6', '#2563eb', '#1d4ed8',
  '#1e40af', '#1e3a8a', '#60a5fa', '#93c5fd',
];

function getActionColor(action) {
  if (action.startsWith('screener_')) {
    const screenerActions = Object.keys(ACTION_LABELS).filter((a) => a.startsWith('screener_'));
    const idx = screenerActions.indexOf(action);
    return SCREENER_COLORS[idx >= 0 ? idx % SCREENER_COLORS.length : 0];
  }
  const watchlistActions = Object.keys(ACTION_LABELS).filter((a) => a.startsWith('watchlist_'));
  const idx = watchlistActions.indexOf(action);
  return WATCHLIST_COLORS[idx >= 0 ? idx % WATCHLIST_COLORS.length : 0];
}

function getActionLabel(action) {
  return ACTION_LABELS[action] || action;
}

const CustomBarTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0].payload;
  return (
    <div style={{
      background: '#2a2a2a',
      border: '1px solid #444',
      borderRadius: 6,
      padding: '10px 14px',
      color: '#fff',
      fontSize: 13,
    }}>
      <p style={{ margin: '0 0 6px 0', fontWeight: 600 }}>{data.label}</p>
      <p style={{ margin: '0 0 3px 0', color: '#aeb9cc' }}>
        Count: <span style={{ color: '#fff', fontWeight: 600 }}>{data.count}</span>
      </p>
      <p style={{ margin: 0, color: '#aeb9cc' }}>
        Unique Users: <span style={{ color: '#fff', fontWeight: 600 }}>{data.unique_users}</span>
      </p>
    </div>
  );
};

const CustomAreaTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{
      background: '#2a2a2a',
      border: '1px solid #444',
      borderRadius: 6,
      padding: '10px 14px',
      color: '#fff',
      fontSize: 13,
    }}>
      <p style={{ margin: '0 0 8px 0', fontWeight: 600, color: '#aeb9cc' }}>{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ margin: '2px 0', color: entry.color }}>
          {getActionLabel(entry.dataKey)}: <span style={{ fontWeight: 600 }}>{entry.value}</span>
        </p>
      ))}
    </div>
  );
};

const CustomBarLabel = ({ x, y, width, height, value }) => {
  if (!value) return null;
  return (
    <text
      x={x + width + 8}
      y={y + height / 2}
      fill="#aeb9cc"
      fontSize={12}
      dominantBaseline="middle"
    >
      {value} users
    </text>
  );
};

function ReportsFeatureAdoption() {
  const [featureData, setFeatureData] = useState(null);
  const [period, setPeriod] = useState('30d');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getFeatureAdoption(period);
      setFeatureData(result);
    } catch (err) {
      setError(err.message || 'Failed to fetch feature adoption data');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePeriodChange = (e) => {
    setPeriod(e.target.value);
  };

  if (loading) {
    return (
      <div className="roles-loading">
        <div className="spinner"></div>
        <p>Loading feature adoption data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reports-error">
        <p>{error}</p>
        <button className="refresh-btn" onClick={fetchData}>Retry</button>
      </div>
    );
  }

  const actionStats = featureData?.action_stats || [];
  const adoptionOverTime = featureData?.adoption_over_time || [];
  const totalUsers = featureData?.total_users || 0;

  // Apply filter
  const filteredStats = actionStats.filter((stat) => {
    if (filter === 'screener') return stat.action.startsWith('screener_');
    if (filter === 'watchlist') return stat.action.startsWith('watchlist_');
    return true;
  });

  // Sort by count descending for bar chart
  const barChartData = [...filteredStats]
    .sort((a, b) => b.count - a.count)
    .map((stat) => ({
      ...stat,
      label: getActionLabel(stat.action),
      color: getActionColor(stat.action),
    }));

  // Top 5 actions for adoption cards and area chart
  const top5Actions = [...filteredStats]
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  // Process adoption_over_time for stacked area chart
  const top5ActionKeys = top5Actions.map((a) => a.action);
  const filteredTimeSeries = adoptionOverTime.filter((entry) => {
    if (filter === 'screener') return entry.action.startsWith('screener_');
    if (filter === 'watchlist') return entry.action.startsWith('watchlist_');
    return true;
  });

  // Group by date, then pivot actions into columns
  const dateMap = {};
  filteredTimeSeries.forEach((entry) => {
    if (!top5ActionKeys.includes(entry.action)) return;
    if (!dateMap[entry.date]) {
      dateMap[entry.date] = { date: entry.date };
      top5ActionKeys.forEach((key) => {
        dateMap[entry.date][key] = 0;
      });
    }
    dateMap[entry.date][entry.action] = entry.users || 0;
  });
  const areaChartData = Object.values(dateMap).sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );

  // Calculate dynamic bar chart height
  const barHeight = 36;
  const barChartHeight = Math.max(400, barChartData.length * barHeight + 60);

  return (
    <div style={{ padding: 10 }}>
      {/* Header with period selector and filter toggles */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 25,
        flexWrap: 'wrap',
        gap: 15,
      }}>
        <h3 style={{ margin: 0, color: '#fdfdfd', fontSize: 22 }}>Feature Adoption</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {/* Filter toggles */}
          <div style={{ display: 'flex', gap: 4 }}>
            {[
              { key: 'all', label: 'All' },
              { key: 'screener', label: 'Screener Only' },
              { key: 'watchlist', label: 'Watchlist Only' },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setFilter(opt.key)}
                style={{
                  padding: '7px 14px',
                  border: filter === opt.key ? '2px solid #218aff' : '2px solid #444',
                  borderRadius: 6,
                  background: filter === opt.key ? 'rgba(33, 138, 255, 0.15)' : '#2a2a2a',
                  color: filter === opt.key ? '#218aff' : '#aeb9cc',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: filter === opt.key ? 600 : 400,
                  transition: 'all 0.2s',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {/* Period selector */}
          <div className="period-selector">
            <label htmlFor="feature-period-select">Period:</label>
            <select
              id="feature-period-select"
              value={period}
              onChange={handlePeriodChange}
              className="period-select"
            >
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
            </select>
            <button className="refresh-btn" onClick={fetchData} disabled={loading}>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {filteredStats.length === 0 ? (
        <p style={{ color: '#aeb9cc', textAlign: 'center', padding: 40 }}>
          No feature adoption data for the selected period and filter.
        </p>
      ) : (
        <>
          {/* Action Breakdown Horizontal Bar Chart */}
          <div style={{
            background: '#212121',
            borderRadius: 10,
            padding: 20,
            border: '1px solid #333',
            marginBottom: 25,
          }}>
            <h4 style={{ margin: '0 0 20px 0', color: '#fdfdfd', fontSize: 18 }}>
              Action Breakdown
            </h4>
            <ResponsiveContainer width="100%" height={barChartHeight}>
              <BarChart
                data={barChartData}
                layout="vertical"
                margin={{ top: 5, right: 100, left: 140, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#333" horizontal={false} />
                <XAxis type="number" tick={{ fill: '#aeb9cc', fontSize: 12 }} axisLine={{ stroke: '#444' }} />
                <YAxis
                  type="category"
                  dataKey="label"
                  tick={{ fill: '#fff', fontSize: 13 }}
                  axisLine={{ stroke: '#444' }}
                  width={130}
                />
                <Tooltip content={<CustomBarTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={22} label={<CustomBarLabel />}>
                  {barChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Adoption Rate Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 16,
            marginBottom: 25,
          }}>
            {top5Actions.map((stat) => {
              const adoptionPct = totalUsers > 0
                ? ((stat.unique_users / totalUsers) * 100).toFixed(1)
                : '0.0';
              const color = getActionColor(stat.action);
              return (
                <div
                  key={stat.action}
                  style={{
                    background: '#212121',
                    borderRadius: 10,
                    padding: 20,
                    border: '1px solid #333',
                    borderLeft: `4px solid ${color}`,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ fontSize: 14, color: '#aeb9cc', marginBottom: 10, fontWeight: 500 }}>
                    {getActionLabel(stat.action)}
                  </div>
                  <div style={{ fontSize: 32, fontWeight: 700, color, lineHeight: 1 }}>
                    {adoptionPct}%
                  </div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                    adoption rate
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: 14,
                    paddingTop: 12,
                    borderTop: '1px solid #333',
                    fontSize: 13,
                  }}>
                    <div>
                      <span style={{ color: '#888' }}>Count</span>
                      <span style={{ display: 'block', color: '#fff', fontWeight: 600 }}>
                        {stat.count.toLocaleString()}
                      </span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ color: '#888' }}>Users</span>
                      <span style={{ display: 'block', color: '#fff', fontWeight: 600 }}>
                        {stat.unique_users.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Feature Adoption Over Time */}
          {areaChartData.length > 0 && (
            <div style={{
              background: '#212121',
              borderRadius: 10,
              padding: 20,
              border: '1px solid #333',
            }}>
              <h4 style={{ margin: '0 0 20px 0', color: '#fdfdfd', fontSize: 18 }}>
                Adoption Over Time (Top 5 Actions)
              </h4>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={areaChartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: '#aeb9cc', fontSize: 12 }}
                    axisLine={{ stroke: '#444' }}
                  />
                  <YAxis
                    tick={{ fill: '#aeb9cc', fontSize: 12 }}
                    axisLine={{ stroke: '#444' }}
                    allowDecimals={false}
                  />
                  <Tooltip content={<CustomAreaTooltip />} />
                  <Legend
                    formatter={(value) => (
                      <span style={{ color: '#aeb9cc', fontSize: 12 }}>
                        {getActionLabel(value)}
                      </span>
                    )}
                  />
                  {top5ActionKeys.map((actionKey) => (
                    <Area
                      key={actionKey}
                      type="monotone"
                      dataKey={actionKey}
                      stackId="1"
                      stroke={getActionColor(actionKey)}
                      fill={getActionColor(actionKey)}
                      fillOpacity={0.3}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ReportsFeatureAdoption;
